export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly field: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface Tokens {
  accessToken: string
  refreshToken: string | null
  expiresIn: number
}

/**
 * Token state lives here rather than in React so that non-component callers -
 * the socket handshake, a retry after refresh - can read it without a hook.
 */
let tokens: Tokens | null = null
let onTokensChanged: ((next: Tokens | null) => void) | null = null

const STORAGE_KEY = 'clicklinx.tokens'

export function loadStoredTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Tokens
    if (typeof parsed?.accessToken !== 'string') return null
    tokens = parsed
    return parsed
  } catch {
    return null
  }
}

export function setTokens(next: Tokens | null): void {
  tokens = next
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Private browsing can refuse storage; the session still works in-memory.
  }
  onTokensChanged?.(next)
}

export function getAccessToken(): string | null {
  return tokens?.accessToken ?? null
}

export function subscribeToTokens(listener: (next: Tokens | null) => void): () => void {
  onTokensChanged = listener
  return () => {
    onTokensChanged = null
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  auth?: boolean
  /** Internal: prevents a refresh loop if the refresh call itself 401s. */
  retrying?: boolean
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = false, retrying = false } = options

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (auth && tokens?.accessToken) headers.Authorization = `Bearer ${tokens.accessToken}`

  let response: Response
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError('network', 'Could not reach the server. Check your connection.', 0)
  }

  if (response.status === 401 && auth && !retrying && tokens?.refreshToken) {
    const refreshed = await tryRefresh()
    if (refreshed) return api<T>(path, { ...options, retrying: true })
    setTokens(null)
  }

  const text = await response.text()
  const payload = text ? (safeParse(text) as Record<string, unknown>) : {}

  if (!response.ok) {
    throw new ApiError(
      typeof payload.error === 'string' ? payload.error : 'unknown',
      typeof payload.message === 'string' ? payload.message : 'Something went wrong.',
      response.status,
      typeof payload.field === 'string' ? payload.field : null,
    )
  }

  return payload as T
}

async function tryRefresh(): Promise<boolean> {
  const refreshToken = tokens?.refreshToken
  if (!refreshToken) return false
  try {
    const result = await api<{ tokens: Tokens }>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      retrying: true,
    })
    // Cognito does not rotate refresh tokens, so keep the existing one when the
    // response omits it - otherwise the session dies after the first refresh.
    setTokens({
      ...result.tokens,
      refreshToken: result.tokens.refreshToken ?? refreshToken,
    })
    return true
  } catch {
    return false
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return {}
  }
}
